import { supabase } from "./supabase.js";

/* Photos live in a private Supabase Storage bucket, one folder per user.
   The database stores object PATHS, never public URLs — the bucket isn't
   public, so a path is useless to anyone who isn't signed in as its owner.
   Rendering turns paths into short-lived signed URLs at read time. */

const BUCKET = "photos";
const AVATAR_BUCKET = "avatars";
const SIGNED_URL_TTL = 60 * 60; // an hour is plenty for one session's viewing

function extensionFor(file) {
  const fromName = file.name?.split(".").pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  return (file.type?.split("/")[1] || "jpg").toLowerCase();
}

/** Uploads one file and returns its storage path. */
export async function uploadPhoto(userId, folder, file) {
  const path = `${userId}/${folder}/${crypto.randomUUID()}.${extensionFor(file)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  return path;
}

export async function deletePhoto(path) {
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

/* Avatars are the one image in this app meant to be seen by other collectors,
   so they live in their own PUBLIC bucket rather than the private one item
   photos use. One object per collector, at a fixed path, replaced in place —
   which leaves no orphans to clean up but does mean the URL never changes, so
   a version stamp goes on the end or browsers keep showing the old face. */

/** Uploads a profile photo and returns its public URL. */
export async function uploadAvatar(userId, file) {
  const path = `${userId}/avatar`;
  const { error } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: true,
    contentType: file.type || undefined,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) throw new Error("Storage returned no public URL for the avatar.");
  return `${data.publicUrl}?v=${Date.now()}`;
}

/** Signs many paths at once. Returns a { path: url } map; paths that fail to
 *  sign are simply absent, so a single missing object can't blank a gallery. */
export async function signPaths(paths) {
  const unique = [...new Set((paths || []).filter(Boolean))];
  if (!unique.length) return {};

  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(unique, SIGNED_URL_TTL);
  if (error) return {};

  const map = {};
  for (const row of data || []) {
    if (row.signedUrl && !row.error) map[row.path] = row.signedUrl;
  }
  return map;
}

/** Rejects obviously wrong files before they cost an upload round trip. */
export function validateImage(file) {
  if (!file.type?.startsWith("image/")) return "That file isn't an image.";
  if (file.size > 10 * 1024 * 1024) return "Images must be under 10 MB.";
  return null;
}
